import { useRecoilCallback } from 'recoil'
import { v4 as uuidv4 } from 'uuid'
import type { EditorTextBoxInput } from '@messenger/lotus/row-store'
import {
  getEditorPageContent,
  saveEditorPageContent,
} from '@/api/editorPageContent'
import { editorPageContentAtomFamily } from '@/atoms/editorTextBoxAtoms'
import { canvasPageIdAtom, unsavedChangesAtom } from '@/atoms/_editorAtoms'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { editorTextBoxDocumentToInput } from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { EditorTextBoxDocument } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { logger, EventCategory } from '@/utils/logger'

export type EditorTextBoxClipboardItem = {
  id: string
  x?: number | string
  y?: number | string
  editorTextBoxDocument?: EditorTextBoxDocument
}

export const useEditorPasteTextBoxFromClipboard = () => {
  const syncCurrentPageToEditorPages = useSyncCurrentPageToEditorPages()
  const handleError = useErrorHandler('useEditorPasteTextBoxFromClipboard')

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (
        clipboardItem: EditorTextBoxClipboardItem,
      ): Promise<string | null> => {
        const document = clipboardItem.editorTextBoxDocument
        if (!document) {
          handleError(
            'This text box was copied before it could be captured — copy it again to paste it',
            new Error(`clipboard box ${clipboardItem.id} has no document`),
          )
          return null
        }
        const pageId = await snapshot.getPromise(canvasPageIdAtom)
        if (!pageId) return null
        const { expectedRevision: _dropped, ...sourceBox } =
          editorTextBoxDocumentToInput(document) as EditorTextBoxInput & {
            expectedRevision?: number
          }
        void _dropped
        const boxId = uuidv4()
        const box: EditorTextBoxInput = {
          ...sourceBox,
          boxId,
          x: (sourceBox.x ?? 0) + 15,
          y: (sourceBox.y ?? 0) + 15,
        }
        try {
          await saveEditorPageContent(pageId, {
            boxes: [box],
            deleteBoxIds: [],
          })
          const { data: bundle } = await getEditorPageContent(pageId)
          await syncCurrentPageToEditorPages()
          set(editorPageContentAtomFamily(pageId), bundle)
          set(unsavedChangesAtom, true)
        } catch (error) {
          handleError(
            'The text box could not be pasted',
            error instanceof Error ? error : new Error(String(error)),
          )
          return null
        }
        logger.info({
          message: 'editor_paste_text_box_success',
          category: EventCategory.INSERT,
          data: { pageId, boxId, sourceItemId: clipboardItem.id },
        })
        return boxId
      },
    [syncCurrentPageToEditorPages, handleError],
  )
}

export default useEditorPasteTextBoxFromClipboard
